---
title: "Как конвертировать PDF в JPG локально в браузере"
description: "Нужно вытащить страницы PDF как картинки? Конвертируйте PDF в JPG прямо в браузере: 72, 150 или 300 DPI, без загрузки файла на сервер."
locale: "ru"
date: "2026-06-03"
tags: ["pdf", "jpg", "локально"]
relatedTools: ["vJpg"]
translations:
  en: "how-to-convert-pdf-to-jpg-locally"
faq:
  - q: "PDF загружается на сервер?"
    a: "Нет. PDF рендерится в браузере через pdf.js, страницы кодируются в JPG на вашем устройстве. В Network tab не должно быть upload-запроса с файлом."
  - q: "Какой DPI выбрать?"
    a: "72 DPI хватит для превью и мессенджера, 150 DPI обычно нормален для сайта или вложения в письмо, 300 DPI имеет смысл для печати и мелкого текста. Чем выше DPI, тем тяжелее JPG."
  - q: "Что будет с многостраничным PDF?"
    a: "Одна страница превращается в один `.jpg`. Если страниц несколько, результат скачивается ZIP-архивом с файлами вида `document-page-01.jpg`."
  - q: "Почему на старом телефоне конвертация может упасть?"
    a: "Каждая страница рендерится в canvas. На больших PDF и 300 DPI браузер может упереться в память, особенно Safari на старых iPhone. Тогда лучше снизить DPI или открыть файл на ноутбуке."
---

import { PdfPageImagesForm } from '@/features/pdf-page-images'

`scan_akt_12_pages.pdf` надо отправить в чат как картинки. Не архивом,
не ссылкой на облако, а просто по одной странице, чтобы человек открыл
с телефона и сразу увидел подпись. Acrobat ради этого ставить смешно,
а онлайн-сервисы для такой мелочи часто требуют загрузить файл к себе.

Ниже форма `PDF → JPG`. Она работает локально: браузер открывает PDF,
рендерит страницы через pdf.js и отдаёт JPG. Для одной страницы скачаете
один файл, для нескольких - ZIP. Лимит исходного PDF - 100 МБ.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <PdfPageImagesForm />
</div>

## DPI без мистики

PDF сам по себе не картинка. Внутри может быть живой текст, векторные
линии, сканы, логотипы, формы. Когда вы делаете JPG, всё это надо
отрисовать в конкретный размер. Тут и появляется DPI.

72 DPI - быстрый режим для превью. Страница получится лёгкой, но мелкий
текст после увеличения будет рыхлым. 150 DPI - нормальная середина для
сайта, письма, мессенджера, карточки товара или инструкции. 300 DPI -
уже тяжёлый режим под печать и мелкие подписи. Если в PDF 40 страниц,
300 DPI легко превратит результат в здоровенный ZIP.

Я бы начинал со 150 DPI. Если нужно просто показать акт, счёт или меню,
этого обычно хватает. Если потом будут печатать страницу с мелкими
таблицами, тогда 300 DPI имеет смысл. Если надо кинуть одну картинку в
Telegram, 72 DPI часто выглядит лучше, чем попытка отправить архив на
полгигабайта.

## Почему локально здесь реально работает

Для PDF в JPG не нужен LibreOffice, Ghostscript на сервере или очередь
воркеров. Браузер и так умеет рисовать на canvas, а pdf.js умеет читать
страницы PDF. Это тот случай, где локальная обработка не маркетинговая
фраза, а нормальная инженерная граница.

Можно открыть DevTools, вкладку Network, загрузить файл в форму и
посмотреть самому. Уходят JS, CSS и worker pdf.js. Сам PDF наружу не
отправляется. Smallpdf, iLovePDF, PDF24 и похожие сервисы часто делают
то же самое через upload на сервер. Для публичной брошюры это неважно.
Для паспорта, договора или внутренней инструкции разница уже не
теоретическая.

## Где конвертация в JPG не лучший ответ

JPG убивает интерактивность PDF. Текст уже нельзя выделить, ссылки не
кликаются, прозрачность и векторная резкость превращаются в растровую
картинку. Если вам нужен документ для архива или юриста, оставьте PDF.
Если нужна картинка для сайта, чата, CRM или превью, JPG подходит.

Отдельно про сканы. Если исходный PDF уже состоит из плохих фотографий,
конвертация не сделает их лучше. Она просто перерисует то, что есть.
Кривой лист останется кривым, серый фон останется серым. Тут сначала
надо пересканировать нормально, а уже потом думать про формат.
